import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

const userDisplay = (u) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username : "—");

export default function StageRotationFormDialog({ open, onClose, rotation, groups, departments, users, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    group: "",
    department: "",
    start_date: "",
    end_date: "",
    sequence: 1,
    supervisor: "",
  });

  useEffect(() => {
    if (rotation) {
      setForm({
        group: rotation.group ?? "",
        department: rotation.department ?? "",
        start_date: rotation.start_date ? rotation.start_date.slice(0, 10) : "",
        end_date: rotation.end_date ? rotation.end_date.slice(0, 10) : "",
        sequence: rotation.sequence ?? 1,
        supervisor: rotation.supervisor ?? "",
      });
    } else {
      setForm({ group: "", department: "", start_date: "", end_date: "", sequence: 1, supervisor: "" });
    }
  }, [rotation, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === "sequence" ? (value ? Number(value) : 1) : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      group: form.group ? Number(form.group) : null,
      department: form.department ? Number(form.department) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      sequence: form.sequence ?? 1,
      supervisor: form.supervisor ? Number(form.supervisor) : null,
    });
  };

  const groupList = groups?.results ?? groups ?? [];
  const departmentList = departments?.results ?? departments ?? [];
  const userList = users?.results ?? users ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rotation ? t("academic.editStageRotation", "Edit stage rotation") : t("academic.assignGroupToDepartment", "Assign group to department")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth required name="group" select label={t("academic.group", "Group")} value={form.group} onChange={handleChange} size="small">
                <MenuItem value="">—</MenuItem>
                {groupList.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.code ? `${g.name} (${g.code})` : g.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required name="department" select label={t("academic.department", "Department")} value={form.department} onChange={handleChange} size="small">
                <MenuItem value="">—</MenuItem>
                {departmentList.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required name="start_date" type="date" label={t("academic.startDate", "Start date")} value={form.start_date} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required name="end_date" type="date" label={t("academic.endDate", "End date")} value={form.end_date} onChange={handleChange} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="sequence" type="number" inputProps={{ min: 1 }} label={t("academic.sequence", "Sequence")} value={form.sequence} onChange={handleChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth name="supervisor" select label={t("academic.supervisor", "Supervisor")} value={form.supervisor} onChange={handleChange} size="small">
                <MenuItem value="">—</MenuItem>
                {userList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{userDisplay(u)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
          <Button type="submit" variant="contained" disabled={!form.group || !form.department || !form.start_date || !form.end_date}>{t("common.save", "Save")}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
